import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';

const initialState = {
  reviews: [],
  userReviews: [],
  loading: false,
  error: null,
  success: false,
  adminReviews: [],
  testimonials: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 8
  },
  stats: {
    all: 0,
    published: 0,
    pending: 0,
    rejected: 0
  }
};

// Async Thunk: Fetch Reviews for a Single Game
export const fetchGameReviews = createAsyncThunk(
  'review/fetchGameReviews',
  async (gameId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/games/${gameId}/reviews`);
      if (res.data.success) {
        return res.data.reviews;
      }
      return rejectWithValue('Failed to fetch reviews');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Fetch Reviews submitted by a specific user
export const fetchUserReviews = createAsyncThunk(
  'review/fetchUserReviews',
  async (userId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/users/${userId}/reviews`);
      if (res.data.success) {
        return res.data.reviews || [];
      }
      return rejectWithValue('Failed to fetch user reviews');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Fetch Reviews for a Single Blog
export const fetchBlogReviews = createAsyncThunk(
  'review/fetchBlogReviews',
  async (blogId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/blogs/${blogId}/reviews`);
      if (res.data.success) {
        return res.data.reviews;
      }
      return rejectWithValue('Failed to fetch blog reviews');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Fetch All Reviews
export const fetchAllReviews = createAsyncThunk(
  'review/fetchAllReviews',
  async ({ search = '', page = 1, limit = 12 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/reviews?search=${search}&page=${page}&limit=${limit}`);
      if (res.data.success) {
        return res.data.reviews;
      }
      return rejectWithValue('Failed to fetch reviews');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { review } = getState();
      if (review.loading || (review.reviews && review.reviews.length > 0)) return false;
      return true;
    }
  }
);

// Async Thunk: Fetch Homepage Testimonials
export const fetchTestimonials = createAsyncThunk(
  'review/fetchTestimonials',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/reviews/testimonials');
      if (res.data.success) {
        return res.data.reviews;
      }
      return rejectWithValue('Failed to fetch testimonials');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Fetch Admin Reviews with pagination, search, status, and tab
export const fetchAdminReviews = createAsyncThunk(
  'review/fetchAdminReviews',
  async ({ page = 1, limit = 8, status = 'ALL', search = '', tab = 'Games' }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/reviews/admin?page=${page}&limit=${limit}&status=${status}&search=${search}&tab=${tab}`);
      if (res.data.success) {
        return res.data;
      }
      return rejectWithValue('Failed to fetch admin reviews');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Submit a Review (Player / Client facing for Game)
export const submitReview = createAsyncThunk(
  'review/submitReview',
  async ({ gameId, rating, comment }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/games/${gameId}/reviews`, { rating, comment });
      if (res.data.success) {
        return res.data.review;
      }
      return rejectWithValue('Failed to submit review');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Submit a Blog Review (Player / Client facing)
export const submitBlogReview = createAsyncThunk(
  'review/submitBlogReview',
  async ({ blogId, rating, comment }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/blogs/${blogId}/reviews`, { rating, comment });
      if (res.data.success) {
        return res.data.review;
      }
      return rejectWithValue('Failed to submit blog review');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Admin Update Review (Changes rating, comments, status)
export const adminUpdateReview = createAsyncThunk(
  'review/adminUpdateReview',
  async ({ reviewId, rating, comment, status }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/reviews/${reviewId}`, { rating, comment, status });
      if (res.data.success) {
        return res.data.review;
      }
      return rejectWithValue('Failed to update review');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Admin Add Review (Creates & sets status directly)
export const adminAddReview = createAsyncThunk(
  'review/adminAddReview',
  async ({ gameId, blogId, rating, comment, status }, { rejectWithValue }) => {
    try {
      const isBlog = !!blogId;
      const endpoint = isBlog ? `/blogs/${blogId}/reviews` : `/games/${gameId}/reviews`;
      const res = await axiosInstance.post(endpoint, { rating, comment });
      if (res.data.success) {
        let review = res.data.review;
        if (status && status !== 'Pending') {
          const updateRes = await axiosInstance.put(`/reviews/${review._id}`, { status });
          if (updateRes.data.success) {
            review = updateRes.data.review;
          }
        }

        if (isBlog) {
          const blogsRes = await axiosInstance.get('/blogs');
          // Find target blog
          const targetBlogs = blogsRes.data?.blogs || blogsRes.data?.data || [];
          const blogItem = targetBlogs.find(b => b._id === blogId);
          return {
            ...review,
            blog: { _id: blogId, title: blogItem ? blogItem.title : 'Admin Blog Post' }
          };
        } else {
          // Lookup game title
          const gamesRes = await axiosInstance.get('/games');
          const gameItem = gamesRes.data?.games?.find(g => g._id === gameId);

          return {
            ...review,
            game: { _id: gameId, title: gameItem ? gameItem.title : 'Blockbuster Game' }
          };
        }
      }
      return rejectWithValue('Failed to add review');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Delete a Review (Supports both client and admin roles)
export const deleteReview = createAsyncThunk(
  'review/deleteReview',
  async (reviewId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/reviews/${reviewId}`);
      if (res.data.success) {
        return reviewId;
      }
      return rejectWithValue('Failed to delete review');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Toggle Like on a Review
export const toggleLikeReview = createAsyncThunk(
  'review/toggleLikeReview',
  async (reviewId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/reviews/${reviewId}/like`);
      if (res.data.success) {
        return { reviewId, likes: res.data.likes, dislikes: res.data.dislikes };
      }
      return rejectWithValue('Failed to toggle like');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Toggle Dislike on a Review
export const toggleDislikeReview = createAsyncThunk(
  'review/toggleDislikeReview',
  async (reviewId, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/reviews/${reviewId}/dislike`);
      if (res.data.success) {
        return { reviewId, likes: res.data.likes, dislikes: res.data.dislikes };
      }
      return rejectWithValue('Failed to toggle dislike');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const reviewSlice = createSlice({
  name: 'review',
  initialState,
  reducers: {
    clearReviewStatus: (state) => {
      state.success = false;
      state.error = null;
    },
    setReviewsList: (state, action) => {
      state.reviews = action.payload;
    },
    addReviewToState: (state, action) => {
      state.reviews.unshift(action.payload);
    },
    updateReviewInState: (state, action) => {
      state.reviews = state.reviews.map(r =>
        (r._id === action.payload._id || r.id === action.payload._id) ? action.payload : r
      );
    },
    deleteReviewFromState: (state, action) => {
      state.reviews = state.reviews.filter(r =>
        r._id !== action.payload && r.id !== action.payload
      );
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Game Reviews (single game)
      .addCase(fetchGameReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGameReviews.fulfilled, (state, action) => {
        state.reviews = action.payload;
        state.loading = false;
      })
      .addCase(fetchGameReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch User Reviews (profile view)
      .addCase(fetchUserReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserReviews.fulfilled, (state, action) => {
        state.userReviews = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Blog Reviews (single blog)
      .addCase(fetchBlogReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogReviews.fulfilled, (state, action) => {
        state.reviews = action.payload;
        state.loading = false;
      })
      .addCase(fetchBlogReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch All Reviews
      .addCase(fetchAllReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllReviews.fulfilled, (state, action) => {
        state.reviews = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Testimonials
      .addCase(fetchTestimonials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTestimonials.fulfilled, (state, action) => {
        state.testimonials = action.payload;
        state.loading = false;
      })
      .addCase(fetchTestimonials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Admin Reviews
      .addCase(fetchAdminReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminReviews.fulfilled, (state, action) => {
        state.adminReviews = action.payload.reviews;
        state.pagination = action.payload.pagination;
        state.stats = action.payload.stats;
        state.loading = false;
      })
      .addCase(fetchAdminReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Submit Review (Player)
      .addCase(submitReview.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(submitReview.fulfilled, (state, action) => {
        state.reviews.unshift(action.payload);
        state.loading = false;
        state.success = true;
      })
      .addCase(submitReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Submit Blog Review (Player)
      .addCase(submitBlogReview.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(submitBlogReview.fulfilled, (state, action) => {
        state.reviews.unshift(action.payload);
        state.loading = false;
        state.success = true;
      })
      .addCase(submitBlogReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Admin Update Review
      .addCase(adminUpdateReview.pending, (state) => {
        state.loading = true;
      })
      .addCase(adminUpdateReview.fulfilled, (state, action) => {
        state.reviews = state.reviews.map(r =>
          (r._id === action.payload._id || r.id === action.payload._id) ? action.payload : r
        );
        state.loading = false;
        state.success = true;
      })
      .addCase(adminUpdateReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Admin Add Review
      .addCase(adminAddReview.pending, (state) => {
        state.loading = true;
      })
      .addCase(adminAddReview.fulfilled, (state, action) => {
        state.reviews.unshift(action.payload);
        state.loading = false;
        state.success = true;
      })
      .addCase(adminAddReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Review (Unified)
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.reviews = state.reviews.filter(r => r.id !== action.payload && r._id !== action.payload);
      })

      // Toggle Like/Dislike
      .addMatcher(
        (action) => action.type === toggleLikeReview.fulfilled.type || action.type === toggleDislikeReview.fulfilled.type,
        (state, action) => {
          const { reviewId, likes, dislikes } = action.payload;
          const review = state.reviews.find(r => (r.id || r._id) === reviewId);
          if (review) {
            review.likes = likes;
            review.dislikes = dislikes;
          }
        }
      );
  }
});

export const {
  clearReviewStatus,
  setReviewsList,
  addReviewToState,
  updateReviewInState,
  deleteReviewFromState
} = reviewSlice.actions;

export default reviewSlice.reducer;
